import React, { useState } from 'react';
import { 
  Upload, 
  Download, 
  FileText, 
  CheckCircle, 
  XCircle, 
  Clock,
  Eye,
  Trash2,
  AlertCircle
} from 'lucide-react';

const Imports = () => {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Données simulées
  const [imports, setImports] = useState([
    {
      id: 1,
      fichier_nom: 'contacts_entreprise.csv',
      total_contacts: 150,
      contacts_importes: 148,
      statut: 'terminé',
      date_import: '2024-06-15T14:30:00',
      errors: 2
    },
    {
      id: 2,
      fichier_nom: 'prospects_2024.xlsx',
      total_contacts: 75,
      contacts_importes: 0,
      statut: 'echec',
      date_import: '2024-06-16T09:15:00',
      errors: 75
    },
    {
      id: 3,
      fichier_nom: 'clients_vip.csv',
      total_contacts: 25,
      contacts_importes: 0,
      statut: 'en_cours',
      date_import: '2024-06-17T11:45:00',
      errors: 0
    },
  ]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    setSelectedFile(file);
    // Ici vous pourriez déclencher l'import
    console.log('Fichier sélectionné:', file);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'terminé':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'echec':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'en_cours':
        return <Clock className="h-4 w-4 text-yellow-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'terminé':
        return 'text-green-600 bg-green-100';
      case 'echec':
        return 'text-red-600 bg-red-100';
      case 'en_cours':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'terminé':
        return 'Terminé';
      case 'echec':
        return 'Échec';
      case 'en_cours':
        return 'En cours';
      default:
        return status;
    }
  };

  const handleDelete = (importId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet import ?')) {
      setImports(imports.filter(i => i.id !== importId));
    }
  };

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Imports de contacts</h1>
          <p className="mt-1 text-sm text-secondary-500">
            Importez vos contacts depuis des fichiers CSV ou Excel
          </p>
        </div>
        <button className="btn btn-secondary">
          <Download className="h-4 w-4 mr-2" />
          Télécharger modèle
        </button>
      </div>

      {/* Zone d'upload */}
      <div className="card">
        <div
          className={`relative border-2 border-dashed rounded-lg p-8 text-center ${
            dragActive
              ? 'border-primary-400 bg-primary-50'
              : 'border-secondary-300 hover:border-secondary-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <Upload className="mx-auto h-12 w-12 text-secondary-400" />
            <div>
              <h3 className="text-lg font-medium text-secondary-900">
                Glissez-déposez vos fichiers ici
              </h3>
              <p className="text-sm text-secondary-500">
                ou cliquez pour sélectionner un fichier
              </p>
            </div>
            <div>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileInput}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="btn btn-primary cursor-pointer"
              >
                Sélectionner un fichier
              </label>
            </div>
            <p className="text-xs text-secondary-400">
              Formats supportés: CSV, XLSX, XLS (max 10MB)
            </p>
          </div>
        </div>

        {selectedFile && (
          <div className="mt-4 p-4 bg-secondary-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <FileText className="h-5 w-5 text-secondary-400 mr-2" />
                <span className="text-sm font-medium text-secondary-900">
                  {selectedFile.name}
                </span>
                <span className="ml-2 text-xs text-secondary-500">
                  ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </span>
              </div>
              <div className="flex space-x-2">
                <button className="btn btn-primary text-sm">
                  Importer maintenant
                </button>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="btn btn-secondary text-sm"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="card">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">
          Instructions d'import
        </h3>
        <div className="space-y-3 text-sm text-secondary-600">
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />
            <span>Le fichier doit contenir les colonnes : nom, prénom, téléphone (obligatoire)</span>
          </div>
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />
            <span>Les colonnes email et entreprise sont optionnelles</span>
          </div>
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />
            <span>Les numéros de téléphone doivent être au format international (+33...)</span>
          </div>
          <div className="flex items-start">
            <AlertCircle className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />
            <span>Les doublons seront automatiquement détectés et ignorés</span>
          </div>
        </div>
      </div>

      {/* Historique des imports */}
      <div className="card">
        <h3 className="text-lg font-medium text-secondary-900 mb-4">
          Historique des imports
        </h3>
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="table">
            <thead>
              <tr>
                <th>Fichier</th>
                <th>Statut</th>
                <th>Total contacts</th>
                <th>Importés</th>
                <th>Erreurs</th>
                <th>Date d'import</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((importItem) => (
                <tr key={importItem.id}>
                  <td className="font-medium text-secondary-900">
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 text-secondary-400 mr-2" />
                      {importItem.fichier_nom}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center">
                      {getStatusIcon(importItem.statut)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(importItem.statut)}`}>
                        {getStatusLabel(importItem.statut)}
                      </span>
                    </div>
                  </td>
                  <td>{importItem.total_contacts}</td>
                  <td className="text-green-600 font-medium">
                    {importItem.contacts_importes}
                  </td>
                  <td className={importItem.errors > 0 ? 'text-red-600 font-medium' : 'text-secondary-500'}>
                    {importItem.errors}
                  </td>
                  <td>{new Date(importItem.date_import).toLocaleString('fr-FR')}</td>
                  <td>
                    <div className="flex space-x-2">
                      <button className="text-primary-600 hover:text-primary-900">
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(importItem.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {imports.length === 0 && (
        <div className="text-center py-12">
          <Upload className="mx-auto h-12 w-12 text-secondary-400" />
          <h3 className="mt-2 text-sm font-medium text-secondary-900">Aucun import</h3>
          <p className="mt-1 text-sm text-secondary-500">
            Aucun fichier n'a encore été importé.
          </p>
        </div>
      )}
    </div>
  );
};

export default Imports;
